import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Shared design tokens + shell (header + footer) for standalone marketing
 * pages that live outside the static marketing.html (Contact Us, Support).
 * Colours/typography mirror /public/_static/marketing.html so the Ratezy
 * dark theme stays consistent across all public surfaces.
 */
export const MK = {
  color: {
    bg: "#07080a",
    bgSoft: "#0b0d12",
    surface: "#12151c",
    surface2: "#171b24",
    border: "#232833",
    borderSoft: "#1a1e28",
    blue700: "#123c9e",
    blue600: "#1b57d6",
    blue500: "#2e7dff",
    blue400: "#5b9bff",
    blue300: "#8fbbff",
    white: "#f7f9fc",
    grey300: "#c3c9d4",
    grey400: "#97a0b0",
    grey500: "#727b8c",
    grey600: "#5c6472",
  },
  fonts: {
    display: "'Sora', sans-serif",
    body: "'Inter', sans-serif",
    mono: "'JetBrains Mono', monospace",
  },
  wrap: {
    width: "100%",
    maxWidth: 1480,
    margin: "0 auto",
    padding: "0 clamp(20px, 4vw, 64px)",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 999,
    border: "1px solid #232833",
    background: "rgba(46,125,255,0.06)",
    color: "#8fbbff",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  h1: {
    fontFamily: "'Sora', sans-serif",
    fontWeight: 700,
    fontSize: "clamp(2rem, 5vw, 3.4rem)",
    lineHeight: 1.08,
    marginTop: 18,
    color: "#f7f9fc",
    letterSpacing: "-0.02em",
  },
  lead: {
    fontSize: "clamp(0.98rem, 1.4vw, 1.08rem)",
    color: "#97a0b0",
    lineHeight: 1.65,
  },
  card: {
    background: "#12151c",
    border: "1px solid #232833",
    borderRadius: 22,
    padding: "clamp(24px, 3.4vw, 36px)",
  },
  cardTitle: {
    fontFamily: "'Sora', sans-serif",
    fontWeight: 600,
    fontSize: "1.25rem",
    color: "#f7f9fc",
  },
  input: {
    width: "100%",
    background: "#0b0d12",
    border: "1px solid #232833",
    color: "#f7f9fc",
    padding: "12px 14px",
    borderRadius: 9,
    fontSize: "0.96rem",
    fontFamily: "'Inter', sans-serif",
    outline: "none",
    transition: "border-color 0.2s ease, background 0.2s ease",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 22px",
    borderRadius: 9,
    border: "none",
    background: "linear-gradient(135deg, #2e7dff 0%, #1b57d6 100%)",
    color: "#fff",
    fontFamily: "'Sora', sans-serif",
    fontWeight: 600,
    fontSize: "0.95rem",
    cursor: "pointer",
    boxShadow: "0 10px 24px -8px rgba(46,125,255,0.55)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  btnGhost: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 18px",
    borderRadius: 9,
    border: "1px solid #232833",
    background: "transparent",
    color: "#c3d9ff",
    fontFamily: "'Sora', sans-serif",
    fontWeight: 500,
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: "border-color 0.2s ease, color 0.2s ease",
  },
};

export default function MarketingShell({ children, active }) {
  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.background = MK.color.bg;
    document.body.style.color = MK.color.white;
    document.body.style.fontFamily = MK.fonts.body;
    document.documentElement.style.background = MK.color.bg;
    // Load Sora + Inter + JetBrains Mono to match marketing.html
    if (!document.getElementById("ratezy-marketing-fonts")) {
      const l = document.createElement("link");
      l.id = "ratezy-marketing-fonts";
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: MK.color.bg, color: MK.color.white, fontFamily: MK.fonts.body }}>
      <Header active={active} />
      {children}
      <Footer />
    </div>
  );
}

function NavLink({ href, label, external, active }) {
  const style = {
    color: active ? MK.color.white : MK.color.grey400,
    fontSize: "0.9rem",
    fontWeight: active ? 600 : 500,
    transition: "color 0.2s ease",
    textDecoration: "none",
  };
  if (external || href.startsWith("http") || href.startsWith("/#")) {
    return <a href={href} style={style} onMouseEnter={(e) => (e.currentTarget.style.color = MK.color.white)} onMouseLeave={(e) => (e.currentTarget.style.color = active ? MK.color.white : MK.color.grey400)}>{label}</a>;
  }
  return <Link to={href} style={style} onMouseEnter={(e) => (e.currentTarget.style.color = MK.color.white)} onMouseLeave={(e) => (e.currentTarget.style.color = active ? MK.color.white : MK.color.grey400)}>{label}</Link>;
}

function Header({ active }) {
  return (
    <header style={{ borderBottom: `1px solid ${MK.color.borderSoft}`, position: "sticky", top: 0, background: "rgba(7,8,10,0.85)", backdropFilter: "blur(12px)", zIndex: 50 }}>
      <nav style={{
        ...MK.wrap,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "20px clamp(20px, 4vw, 64px)",
        gap: 20,
      }}>
        <Link to="/" data-testid="nav-brand" style={{ display: "inline-flex", alignItems: "center" }}>
          <img src="/ratezy-logo.webp" alt="Ratezy" style={{ height: 26, display: "block" }} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 28 }} className="hide-on-mobile">
          <NavLink href="/#why-ratings" label="Why it matters" />
          <NavLink href="/#how" label="How it works" />
          <NavLink href="/#audience" label="Who it's for" />
          <NavLink href="/#pricing" label="Pricing" />
          <NavLink href="/#faq" label="FAQ" />
          <NavLink href="/contact" label="Contact us" active={active === "contact"} />
        </div>
        <a href="/#cta" data-testid="nav-book-demo" className="mk-cta-desktop" style={{
          padding: "8px 16px",
          borderRadius: 999,
          background: "linear-gradient(135deg, #2e7dff 0%, #1b57d6 100%)",
          color: "#fff",
          fontSize: "0.82rem",
          fontFamily: MK.fonts.display,
          fontWeight: 600,
          textDecoration: "none",
          boxShadow: "0 8px 20px -6px rgba(46,125,255,0.55)",
        }}>
          Book a free demo
        </a>
        <Link to="/contact" data-testid="nav-contact-mobile" className="mk-cta-mobile" style={{
          padding: "7px 14px",
          borderRadius: 999,
          border: "1px solid rgba(139,187,255,0.45)",
          color: "#c3d9ff",
          fontSize: "0.78rem",
          fontFamily: MK.fonts.display,
          fontWeight: 600,
          textDecoration: "none",
          background: "transparent",
          whiteSpace: "nowrap",
        }}>
          Contact us
        </Link>
      </nav>
      <style>{`
        .mk-cta-mobile { display: none; }
        @media (max-width: 900px) {
          .hide-on-mobile { display: none !important; }
          .mk-cta-desktop { display: none !important; }
          .mk-cta-mobile { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
}

function Footer() {
  return (
    <footer style={{ borderTop: `1px solid ${MK.color.borderSoft}`, padding: "56px 0 34px", marginTop: 60 }}>
      <div style={MK.wrap}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          flexWrap: "wrap", gap: 32, marginBottom: 36,
        }}>
          <div style={{ maxWidth: 300 }}>
            <img src="/ratezy-logo.webp" alt="Ratezy" style={{ height: 22, marginBottom: 12 }} />
            <p style={{ color: MK.color.grey500, fontSize: "0.88rem" }}>
              Reviews made easy. Ratezy is a product by Digital Table, helping local businesses across Australia turn happy customers into genuine Google reviews.
            </p>
          </div>
          <div style={{ display: "flex", gap: 56, flexWrap: "wrap" }}>
            <FootCol title="Product" links={[
              { href: "/#how", label: "How it works" },
              { href: "/#audience", label: "Who it's for" },
              { href: "/#pricing", label: "Pricing" },
              { href: "/#faq", label: "FAQ" },
            ]} />
            <FootCol title="Company" links={[
              { href: "/contact", to: "/contact", label: "Contact us" },
              { href: "/support", to: "/support", label: "Support" },
              { href: "/#", label: "Privacy policy" },
              { href: "/#", label: "Terms of use" },
              { href: "/#cta", label: "Book a demo" },
            ]} />
          </div>
        </div>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12, paddingTop: 26, borderTop: `1px solid ${MK.color.borderSoft}`,
          fontSize: "0.8rem", color: MK.color.grey600,
        }}>
          <span>© 2026 Ratezy, a Digital Table product. All rights reserved.</span>
          <span>Made for local businesses across Australia</span>
        </div>
      </div>
    </footer>
  );
}

function FootCol({ title, links }) {
  return (
    <div>
      <h5 style={{
        fontFamily: MK.fonts.mono, fontSize: "0.74rem", textTransform: "uppercase",
        letterSpacing: "0.05em", color: MK.color.grey600, marginBottom: 14,
      }}>{title}</h5>
      <ul style={{ display: "flex", flexDirection: "column", gap: 10, listStyle: "none", padding: 0, margin: 0 }}>
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} style={{ color: MK.color.grey400, fontSize: "0.88rem", textDecoration: "none" }}>{l.label}</Link>
            ) : (
              <a href={l.href} style={{ color: MK.color.grey400, fontSize: "0.88rem", textDecoration: "none" }}>{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
